import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { useLanguage } from '../i18n/LanguageProvider'
import { withLang } from '../i18n/urlLang'
import LanguageToggle from './LanguageToggle'
import EnglishBanner from './EnglishBanner'
import type { MsgKey } from '../i18n/types'

// href = in-page anchor (homepage sections) → <a>; route = React Router path → <Link>.
// Phase 95: the array holds translation KEYS, not labels — `t` does not exist at
// module level, and the label is resolved at render time.
const navLinks: { key: MsgKey; href?: string; route?: string }[] = [
  { href: '#concept', key: 'nav.concept' },
  { href: '#reservation', key: 'nav.reserve' },
  { href: '#tarifs', key: 'nav.pricing' },
  { href: '#packs', key: 'nav.packs' },
  { href: '#comment-venir', key: 'nav.access' },
  { route: '/blog', key: 'nav.blog' },
  { href: '#contact', key: 'nav.contact' },
]

export default function Header({ offHome = false }: { offHome?: boolean }) {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { user, loading, signOut, role, roleLoading } = useAuth()
  const { t, lang } = useLanguage()
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!accountMenuOpen) return

    function onClickOutside(e: MouseEvent) {
      const target = e.target as Element
      if (!target.closest('[data-account-menu]')) {
        setAccountMenuOpen(false)
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setAccountMenuOpen(false)
    }

    window.addEventListener('click', onClickOutside)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('click', onClickOutside)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [accountMenuOpen])

  // D-05 (Phase 97) — les cinq entrées de navLinks sans `route` sont des <a>
  // BRUTS : le `basename` du routeur ne les touche pas, contrairement aux
  // <Link>. Depuis une page anglaise, `'/'` menait donc à l'accueil FRANÇAIS.
  // `withLang` est la source unique du préfixe (T-97-03) : elle ne rend que des
  // chemins relatifs, jamais un schéma ni un hôte. Ne pas la remplacer par une
  // concaténation ad hoc. Sur l'accueil (`offHome` faux) le préfixe reste vide
  // dans les deux langues — une ancre interne ne doit pas devenir absolue.
  // Une seule variable : la barre de navigation et le menu mobile la partagent.
  const linkPrefix = offHome ? withLang('/', lang) : ''
  // "Espace admin" should read as active (accent) only when actually under /admin,
  // not permanently — otherwise it looks active on every page (27/05 report).
  const { pathname } = useLocation()
  const onAdminRoute = pathname.startsWith('/admin')
  // Role badge shown next to the name; only once resolved (no "name → name (admin)" flicker).
  const roleBadge = !roleLoading && role ? role : null

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? 'bg-bg/95 backdrop-blur-sm border-b border-border' : ''
      }`}
    >
      <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="font-display font-[800] text-text text-sm tracking-[0.15em] uppercase hover:text-text"
        >
          SWEATSTUDIO
        </Link>

        <nav className="hidden md:block">
          <ul className="flex gap-8 list-none m-0 p-0">
            {navLinks.map((item) => (
              <li key={item.key}>
                {item.route ? (
                  <Link
                    to={item.route}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
                  >
                    {t(item.key)}
                  </Link>
                ) : (
                  <a
                    href={`${linkPrefix}${item.href}`}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
                  >
                    {t(item.key)}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <LanguageToggle />

          <div className="relative" data-account-menu>
            {loading ? (
              <div className="h-5 w-24 bg-surface/40 rounded-[4px] animate-pulse" aria-hidden="true" />
            ) : user ? (
              <>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  aria-label={t('account.menu')}
                  onClick={() => setAccountMenuOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm text-text-muted hover:text-text transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-[4px] px-2 py-1"
                >
                  <span className="max-w-[22ch] truncate">
                    {user.email?.split('@')[0]}
                    {roleBadge && <span className="text-accent"> ({roleBadge})</span>}
                  </span>
                  <span
                    className={`inline-block transition-transform duration-200 ${accountMenuOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>
                {accountMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-2 min-w-[200px] bg-surface border border-border rounded-[4px] py-2 shadow-lg"
                  >
                    <div
                      role="none"
                      className="px-4 py-2 text-xs text-text-muted border-b border-border truncate"
                    >
                      {user.email}
                    </div>
                    <Link
                      role="menuitem"
                      to="/mon-compte"
                      onClick={() => setAccountMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-text hover:bg-bg transition-colors focus-visible:outline-none focus-visible:bg-bg"
                    >
                      {t('account.myAccount')}
                    </Link>
                    {/* Phase 109 (FACT-01) — le libellé vient du catalogue
                        PARTAGÉ : `Header` est eager, lire `pages/billing/*`
                        tirerait ce catalogue dans le chunk d'entrée que tout
                        visiteur télécharge. Rien ici ne va dans `navLinks` :
                        c'est la nav PUBLIQUE (T-109-28). */}
                    <Link
                      role="menuitem"
                      to="/mes-factures"
                      onClick={() => setAccountMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-text hover:bg-bg transition-colors focus-visible:outline-none focus-visible:bg-bg"
                    >
                      {t('account.myInvoices')}
                    </Link>
                    <Link
                      role="menuitem"
                      to="/dashboard"
                      onClick={() => setAccountMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-text hover:bg-bg transition-colors focus-visible:outline-none focus-visible:bg-bg"
                    >
                      {t('account.myDashboard')}
                    </Link>
                    {role && (
                      <Link
                        role="menuitem"
                        to="/admin"
                        onClick={() => setAccountMenuOpen(false)}
                        className={`block w-full text-left px-4 py-2 text-sm ${onAdminRoute ? 'text-accent' : 'text-text'} hover:bg-bg transition-colors focus-visible:outline-none focus-visible:bg-bg`}
                      >
                        {t('account.admin')}
                      </Link>
                    )}
                    <button
                      role="menuitem"
                      type="button"
                      onClick={async () => {
                        setAccountMenuOpen(false)
                        await signOut()
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-text hover:bg-bg transition-colors cursor-pointer focus-visible:outline-none focus-visible:bg-bg"
                    >
                      {t('account.signOut')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Link
                to="/login"
                className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
              >
                {t('account.login')}
              </Link>
            )}
          </div>
        </div>

        <button
          className="md:hidden flex flex-col gap-[5px] p-2 bg-transparent border-none cursor-pointer"
          aria-label={t('nav.menu')}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className={`block w-5 h-[2px] bg-text transition-transform duration-200 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`block w-5 h-[2px] bg-text transition-opacity duration-200 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-5 h-[2px] bg-text transition-transform duration-200 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>
      </div>

      {menuOpen && (
        <nav className="md:hidden bg-bg/95 backdrop-blur-sm border-b border-border">
          <ul className="flex flex-col items-center gap-4 py-6 list-none m-0 p-0">
            {navLinks.map((item) => (
              <li key={item.key}>
                {item.route ? (
                  <Link
                    to={item.route}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t(item.key)}
                  </Link>
                ) : (
                  <a
                    href={`${linkPrefix}${item.href}`}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t(item.key)}
                  </a>
                )}
              </li>
            ))}
            {/* D-04 — la bascule reste atteignable au doigt comme au clavier. */}
            <li>
              <LanguageToggle />
            </li>
          </ul>

          {!loading && (
            <div className="border-t border-border pt-4 pb-6 flex flex-col items-center gap-2">
              {user ? (
                <>
                  <span className="text-text-muted text-sm truncate max-w-[260px]">
                    {user.email}
                    {roleBadge && <span className="text-accent"> ({roleBadge})</span>}
                  </span>
                  <Link
                    to="/mon-compte"
                    onClick={() => setMenuOpen(false)}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
                  >
                    {t('account.myAccount')}
                  </Link>
                  {/* Le jumeau mobile de l'entrée ci-dessus. Posée au bureau
                      seulement, elle rendrait « un clic depuis n'importe quelle
                      page » faux sur téléphone — c'est-à-dire là où
                      l'opératrice a cherché la page (T-109-31). */}
                  <Link
                    to="/mes-factures"
                    onClick={() => setMenuOpen(false)}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
                  >
                    {t('account.myInvoices')}
                  </Link>
                  <Link
                    to="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
                  >
                    {t('account.myDashboard')}
                  </Link>
                  {role && (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      className={`text-sm font-medium uppercase tracking-[0.1em] transition-colors duration-200 ${onAdminRoute ? 'text-accent' : 'text-text-muted hover:text-accent'}`}
                    >
                      {t('account.admin')}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      setMenuOpen(false)
                      await signOut()
                    }}
                    className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200 cursor-pointer"
                  >
                    {t('account.signOut')}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="text-text-muted text-sm font-medium uppercase tracking-[0.1em] hover:text-accent transition-colors duration-200"
                >
                  {t('account.login')}
                </Link>
              )}
            </div>
          )}
        </nav>
      )}

      {/* D-07 — UNE seule instance, contrairement au sélecteur qui en a une
          bureau et une mobile : le bandeau est pleine largeur et n'a aucune
          raison d'être dupliqué. Il se rend sous la barre de navigation, dans
          le flux, et décide tout seul de ne rien rendre. */}
      <EnglishBanner />
    </header>
  )
}
